import { Controller } from "@hotwired/stimulus"

/**
 * Primary sign navigation control: kebab (default / “closed”) vs ✕ (“open” / away from home).
 * Syncs from `signNav:sync` (see sign_swap_controller) after each Turbo frame swap.
 * Animation: CSS `.menu-toggle.is-open` (unchanged).
 */
export default class extends Controller {
  static targets = ["button", "label"]

  connect() {
    this.isOpen = false
    this.boundSignNavSync = this.onSignNavSync.bind(this)
    window.addEventListener("signNav:sync", this.boundSignNavSync)
  }

  disconnect() {
    window.removeEventListener("signNav:sync", this.boundSignNavSync)
  }

  /**
   * Drive chrome from the loaded section (inner `data-welcome-section` after each swap).
   */
  onSignNavSync(event) {
    const section = event.detail?.section
    if (section === "prompt") {
      this.applyDefaultChrome()
    } else {
      this.applyAwayChrome()
    }
  }

  /** Kebab ··· — on the main sign (prompt). */
  applyDefaultChrome() {
    this.isOpen = false
    this.buttonTarget.setAttribute("aria-expanded", "false")
    this.buttonTarget.setAttribute("aria-label", "Toggle sign navigation")
    this.labelTarget.textContent = "Open navigation"
    this.element.classList.remove("is-open")
  }

  /** ✕ — not on default; same visuals as “menu opened” without loading the menu by itself. */
  applyAwayChrome() {
    this.isOpen = true
    this.buttonTarget.setAttribute("aria-expanded", "true")
    this.buttonTarget.setAttribute("aria-label", "Back to main sign")
    this.labelTarget.textContent = "Close"
    this.element.classList.add("is-open")
  }

  markClosed() {
    // Legacy hook on menu links; `signNav:sync` after the frame swap sets kebab vs ✕.
  }

  toggle(event) {
    if (this.isOpen) {
      this.close(event)
      window.dispatchEvent(new CustomEvent("menuToggle:toggleClose"))
    } else {
      this.open()
    }
  }

  /** From default: open the sticky-note menu (loads menu frame). */
  open() {
    this.applyAwayChrome()
    window.dispatchEvent(new CustomEvent("menuToggle:open"))
  }

  close() {
    this.applyDefaultChrome()
  }

  handleKeydown(event) {
    if (event.key === "Escape" && this.isOpen) {
      this.close()
      window.dispatchEvent(new CustomEvent("menuToggle:toggleClose"))
    }
  }
}
